import {
  usePrefetchedSuspenseQueries,
  type DataTag,
  type QueryKey,
} from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import type {
  DrillMorphemeSlice,
  GraphemeMorphemeMnemonicsPanelMorphemeSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { getDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import {
  useDrillMorphemeSliceQueryOptions,
  useMorphemeMnemonicsPanelMorphemeSliceQueryOptions,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useResolvedQuery } from "@-/dev.improve.haniverse.lib.query-options";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { MorphemeNotFound } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.not-found";
import {
  useFetchIsPinyinLearned,
  useProgrammaticGraphemePinyinAudio,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.audio";
import {
  DrillPageContextProvider,
  GraphemeDrillContextProvider,
  type GraphemeDrillType,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { buildGraphemeDrillExpectedAnswerSet } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.submit";
import { useMemo, type ReactNode } from "react";

/** A grapheme drill's providers, below its gate: both slices are non-null by construction. */
function GraphemeDrillProviders({
  character,
  children,
  drillType,
  front,
  frontQueryKey,
  morphemeHrefObject,
  slice,
  sliceQueryKey,
}: {
  character: string;
  children: ReactNode;
  drillType: GraphemeDrillType;
  front: DrillMorphemeSlice;
  frontQueryKey: DataTag<QueryKey, DrillMorphemeSlice | null, Error>;
  morphemeHrefObject: MorphemeHrefObject;
  slice: GraphemeMorphemeMnemonicsPanelMorphemeSlice;
  sliceQueryKey: DataTag<QueryKey, GraphemeMorphemeMnemonicsPanelMorphemeSlice | null, Error>;
}) {
  const resolvedFront = useResolvedQuery(frontQueryKey, front);
  const resolvedSlice = useResolvedQuery(sliceQueryKey, slice);

  // Pinyin audio for the revealed card. Created ONCE per card here (a single `useAudioPlayer`) and
  // handed to the shared drill context so the one `useRevealMutation` mutation can fire it —
  // `useRevealMutation` is instantiated by several consumers (footer button, background Pressable,
  // tap-to-reveal hint, …), so building the player inside it would spin up a redundant player per
  // consumer. We use the lower-level grapheme-pinyin hook (NOT useLearnMnemonicsProgrammaticAudio):
  // this lives on the drill front, which blurs the instant reveal pushes the card back, and the
  // mnemonics wrapper stops playback on blur — cutting the audio off. The drill front stays mounted
  // (pushed-over), so its player survives the nav.
  const [isMutedOverride] = useContextSelectors(NavContext, (c) => c.isMutedOverride);
  const { playForPinyin, playSequence } = useProgrammaticGraphemePinyinAudio(slice, {
    isMuted: isMutedOverride,
  });
  const fetchIsPinyinLearned = useFetchIsPinyinLearned();
  // Unless the learner turned auto-play off (the Learn More menu's Sound options), a revealed card
  // speaks the readings the learner can read, every initial, final and tone of them learned: a
  // pronunciation card drills ONE reading of the glyph, so it speaks only that reading; a meaning
  // card speaks the glyph's readable readings. The setting and the sounds are read at reveal time.
  const playRevealAudio = () => {
    if (!getDeviceSetting("isAutoplayEnabled")) {
      return;
    }
    void (async () => {
      const isPinyinLearned = await fetchIsPinyinLearned();
      if (drillType.kind !== "pronunciation") {
        await playSequence(isPinyinLearned);
      } else if (isPinyinLearned(drillType.pinyin)) {
        await playForPinyin(drillType.pinyin);
      }
    })();
  };

  const expectedAnswerSet = useMemo(
    () => buildGraphemeDrillExpectedAnswerSet({ drillType, slice }),
    [drillType, slice],
  );

  return (
    <GraphemeDrillContextProvider drillType={drillType} front={resolvedFront} slice={resolvedSlice}>
      <DrillPageContextProvider
        character={character}
        drillType={drillType}
        expectedAnswerSet={expectedAnswerSet}
        morphemeHrefObject={morphemeHrefObject}
        playRevealAudio={playRevealAudio}
        sentenceDrill={null}
      >
        {children}
      </DrillPageContextProvider>
    </GraphemeDrillContextProvider>
  );
}

/**
 * The gate for a GRAPHEME drill: the one observer of its front slice and mnemonics slice. Either
 * can be `null` (not in the data, or not in the bundled offline data after an offline-mode switch),
 * which renders `MorphemeNotFound`; below the gate both are non-null.
 */
export function GraphemeDrillGateSuspense({
  character,
  children,
  drillType,
  morphemeHrefObject,
}: {
  character: string;
  children: ReactNode;
  drillType: GraphemeDrillType;
  morphemeHrefObject: MorphemeHrefObject;
}) {
  const frontQueryOptions = useDrillMorphemeSliceQueryOptions({ front: character });
  const sliceQueryOptions = useMorphemeMnemonicsPanelMorphemeSliceQueryOptions(character);
  const [{ data: front }, { data: slice }] = usePrefetchedSuspenseQueries({
    queries: [frontQueryOptions, sliceQueryOptions],
  });

  if (front === null || slice === null) {
    return <MorphemeNotFound morpheme={character} />;
  }

  return (
    <GraphemeDrillProviders
      character={character}
      drillType={drillType}
      front={front}
      frontQueryKey={frontQueryOptions.queryKey}
      morphemeHrefObject={morphemeHrefObject}
      slice={slice}
      sliceQueryKey={sliceQueryOptions.queryKey}
    >
      {children}
    </GraphemeDrillProviders>
  );
}
